import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  useImportPtoEquipmentFileMutation,
  useSavePtoEquipmentBulkMutation,
  type PtoEquipment,
  type PtoEquipmentAiParam,
  type PtoEquipmentAiSummary,
  type PtoEquipmentBody,
  type PtoEquipmentBulkResult,
  type PtoEquipmentImport,
  type PtoImportedEquipment,
} from '@shared/api';
import { matchesAccept } from '@shared/lib/pickFiles';
import { AnalysisBar } from '@shared/ui/AnalysisBar';
import { Button } from '@shared/ui/Button';
import { FileDropZone } from '@shared/ui/FileDropZone';
import {
  Actions,
  Banner,
  DataTable,
  Dialog,
  Muted,
  TableWrap,
  Tag,
  fmtDate,
  fmtNum,
} from '../kfo/ui';
import { isAbortError } from '../rdImport/errors';
import { AiMarkButton } from './AiMark';
import {
  AI_NUMERIC_FIELDS,
  EQUIPMENT_KINDS,
  EQ_CHARACTERISTICS_MAX,
  EQ_COUNT_ERROR,
  EQ_NAME_MAX,
  EQ_NOTES_MAX,
  MAX_EQUIPMENT_ROWS,
  aiFieldDetails,
  aiTextRemains,
  bulkErrorText,
  clipText,
  emptyEqRow,
  eqFieldErrors,
  eqNumError,
  equipmentErrorText,
  fillFromRegistry,
  hasFileNote,
  importErrorText,
  isEqCount,
  isLiftingKind,
  mergeFileNote,
  normName,
  numToInput,
  oneLine,
  parseDecimal,
  plural,
  pointsWord,
  textLength,
  verifiedPatch,
  withoutAi,
  type ChartPointDraft,
  type EqRowDraft,
  type EquipmentAiField,
  type EquipmentNumField,
} from './model';
import {
  CellArea,
  CellInput,
  CellMark,
  CellSelect,
  ErrorList,
  LinkButton,
  WarnBox,
  WizardFooter,
} from './ui';
import { useNow } from './useNow';

/**
 * Перечень техники: Excel и CSV. Книгу Excel 97–2003 (.xls) сервер не
 * принимает — её узнаём по содержимому до отправки; .xlsx под именем .xls
 * отправляем как есть.
 */
const ACCEPT_EQUIPMENT_FILE = '.xlsx,.xlsm,.xls,.csv';
/** Файл — не больше, как на сервере. */
const MAX_FILE_MB = 10;
/** Записей в одной загрузке в справочник — не больше, как на сервере. */
const MAX_BULK = 200;
/** Ошибок проверки строк показываем не больше — дальше «…и ещё N». */
const MAX_SHOWN_ERRORS = 8;

/** Подпись двоичной книги Excel 97–2003 (OLE2). */
const OLE_MAGIC = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];
/** Тот же текст, что у сервера. */
const OLD_XLS_TEXT =
  'Старый формат .xls (Excel 97–2003) не принимаем — откройте файл в Excel и сохраните как .xlsx (Файл → Сохранить как → Книга Excel).';
const WRONG_TYPE_TEXT = 'нужен файл Excel (.xlsx, .xlsm) или CSV с перечнем техники.';

/** Книга Excel 97–2003 — по первым 8 байтам; не прочитали — решит сервер. */
async function isOldExcel(file: File): Promise<boolean> {
  try {
    const head = new Uint8Array(await file.slice(0, OLE_MAGIC.length).arrayBuffer());
    return head.length === OLE_MAGIC.length && OLE_MAGIC.every((b, i) => head[i] === b);
  } catch {
    return false;
  }
}

/** Что программа узнала в колонке файла. */
const COLUMN_LABEL: Record<string, string> = {
  name: 'наименование',
  model: 'марка',
  kind: 'вид',
  notes: 'характеристики',
  count: 'количество',
  lift_capacity_t: 'г/п',
  max_reach_m: 'вылет',
  lift_height_m: 'высота подъёма',
  purpose: 'назначение',
  mobilization_date: 'дата мобилизации',
  mobilization_method: 'способ мобилизации',
};

const NUM_LABEL: Record<EquipmentNumField, string> = {
  lift_capacity_t: 'Г/п max, т',
  max_reach_m: 'Вылет max, м',
  lift_height_m: 'Высота подъёма, м',
};

const KIND_CODES = new Set(EQUIPMENT_KINDS.map(([k]) => k));

type Tone = 'neutral' | 'warning' | 'danger' | 'success' | 'info';

const RESULT_META: Record<
  PtoEquipmentBulkResult['status'],
  { label: string; tone: Tone; title: string }
> = {
  created: { label: 'создано', tone: 'success', title: 'Новая запись справочника' },
  filled: {
    label: 'дозаполнено',
    tone: 'info',
    title: 'Запись с таким наименованием уже была — заполнены только её пустые поля',
  },
  unchanged: {
    label: 'без изменений',
    tone: 'neutral',
    title: 'Запись с таким наименованием уже была, пустых полей для дозаполнения нет',
  },
  error: { label: 'ошибка', tone: 'danger', title: 'Строка не сохранена' },
};

/** Строка файла на проверке: значения — строками, пока инженер правит. */
interface ReviewRow {
  key: string;
  /** Строка как пришла с сервера: подсказки ИИ, пометки, «из файла». */
  src: PtoImportedEquipment;
  /** Отмечена — пойдёт в перечень мастера / в справочник. */
  take: boolean;
  name: string;
  kind: string;
  characteristics: string;
  lift_capacity_t: string;
  max_reach_m: string;
  lift_height_m: string;
  count: string;
  /** Поля из справочных данных ИИ; правка поля или «ИИ ×» снимает пометку. */
  ai_fields: string[];
  /** Мастер: связь с записью справочника (как выбор из подсказки в таблице мастера). */
  regId: string | null;
  /** Вид, характеристики и ГВХ взяты из справочника, а не из файла. */
  fromRegistry: boolean;
  /** ГВХ записи справочника. */
  load_chart: ChartPointDraft[];
  /** Наименование, для которого получены вид и характеристики (файл или справочник). */
  valuesFor: string;
  /** Итог загрузки в справочник; `null` — ещё не отправлялась. */
  result: PtoEquipmentBulkResult | null;
}

type RowValues = Pick<
  ReviewRow,
  | 'name'
  | 'kind'
  | 'characteristics'
  | EquipmentNumField
  | 'ai_fields'
  | 'regId'
  | 'fromRegistry'
  | 'load_chart'
  | 'valuesFor'
>;

/** Значения строки из файла (с дополнением ИИ). */
function fileValues(src: PtoImportedEquipment): RowValues {
  return {
    name: src.name,
    kind: src.kind,
    characteristics: src.characteristics ?? '',
    lift_capacity_t: numToInput(src.lift_capacity_t),
    max_reach_m: numToInput(src.max_reach_m),
    lift_height_m: numToInput(src.lift_height_m),
    ai_fields: [...src.ai_fields],
    regId: null,
    fromRegistry: false,
    load_chart: [],
    valuesFor: src.name,
  };
}

/** Значения из записи справочника — как при выборе в таблице мастера (`fillFromRegistry`). */
function registryValues(e: PtoEquipment): RowValues {
  const f = fillFromRegistry(emptyEqRow(), e);
  return {
    name: f.name,
    kind: f.kind,
    characteristics: f.characteristics,
    lift_capacity_t: f.lift_capacity_t,
    max_reach_m: f.max_reach_m,
    lift_height_m: f.lift_height_m,
    ai_fields: f.ai_fields,
    regId: e.id,
    fromRegistry: true,
    load_chart: f.load_chart,
    valuesFor: e.name,
  };
}

function toReviewRow(
  src: PtoImportedEquipment,
  i: number,
  registryMatch: PtoEquipment | undefined,
): ReviewRow {
  return {
    key: `imp-${i}-${src.row}`,
    src,
    take: true,
    count: src.count != null && src.count > 0 ? String(src.count) : '1',
    result: null,
    ...(registryMatch ? registryValues(registryMatch) : fileValues(src)),
  };
}

/** Строка уже в справочнике (создана/дозаполнена/без изменений) — повторно не шлём. */
const isDone = (r: ReviewRow) => r.result !== null && r.result.status !== 'error';

/** Числа справочных данных ИИ в тексте характеристики (крановые поля в текст не входят). */
function aiValuesOf(r: ReviewRow): number[] {
  if (r.fromRegistry) return [];
  return r.src.ai_params
    .filter((p) => !(AI_NUMERIC_FIELDS as readonly string[]).includes(p.key))
    .map((p) => p.value);
}

/** Пометки ИИ только у заполненных полей — как их хранит сервер. */
function aiFieldsOf(r: ReviewRow): string[] {
  const filled: Record<EquipmentAiField, boolean> = {
    kind: r.kind !== 'other',
    characteristics: !!r.characteristics.trim(),
    lift_capacity_t: !!r.lift_capacity_t.trim(),
    max_reach_m: !!r.max_reach_m.trim(),
    lift_height_m: !!r.lift_height_m.trim(),
  };
  return r.ai_fields.filter((f) => filled[f as EquipmentAiField] === true);
}

function rowErrors(r: ReviewRow, wizard: boolean): string[] {
  const errs = eqFieldErrors(r);
  if (wizard && !isEqCount(r.count)) errs.push(EQ_COUNT_ERROR);
  return errs;
}

/** Строка → строка таблицы техники мастера (вызывать после `rowErrors`). */
function toEqRow(r: ReviewRow): EqRowDraft {
  const date = (r.src.mobilization_date ?? '').slice(0, 10);
  return {
    ...emptyEqRow(),
    equipment_id: r.regId,
    name: oneLine(r.name),
    kind: r.kind,
    characteristics: oneLine(r.characteristics),
    lift_capacity_t: r.lift_capacity_t.trim(),
    max_reach_m: r.max_reach_m.trim(),
    lift_height_m: r.lift_height_m.trim(),
    load_chart: r.load_chart,
    count: r.count.trim(),
    mobilization_date: /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : '',
    mobilization_method: oneLine(r.src.mobilization_method ?? ''),
    purpose: oneLine(r.src.purpose ?? ''),
    ai_fields: aiFieldsOf(r),
    ai_values: aiValuesOf(r),
  };
}

/** Строка → запись справочника; текст файла — в примечание, если его нет в характеристике. */
function toBody(r: ReviewRow): PtoEquipmentBody {
  const note = r.src.file_note?.trim() ?? '';
  const characteristics = oneLine(r.characteristics);
  return {
    name: oneLine(r.name),
    kind: r.kind,
    characteristics: characteristics || null,
    lift_capacity_t: parseDecimal(r.lift_capacity_t),
    max_reach_m: parseDecimal(r.max_reach_m),
    lift_height_m: parseDecimal(r.lift_height_m),
    notes: note && !hasFileNote(characteristics, note) ? clipText(note, EQ_NOTES_MAX) : null,
    ai_fields: aiFieldsOf(r),
  };
}

const withUnit = (v: number, unit: string) => `${fmtNum(v)}${unit ? ` ${unit}` : ''}`;

/** «Эксплуатационная масса 24,5 т (проверочный ИИ: 25 т)». */
function paramLine(p: PtoEquipmentAiParam): string {
  return `${p.label} ${withUnit(p.value, p.unit)} (проверочный ИИ: ${withUnit(p.check, p.unit)})`;
}

/** Подробности к «ИИ ×»: что дописано и что ответил проверочный ИИ. */
function aiTitle(field: EquipmentAiField, r: ReviewRow): string {
  // Из справочника — подробностей проверки нет, только сама пометка записи.
  if (r.fromRegistry) return aiFieldDetails(field);
  if (field === 'kind') return 'Вид техники определил ИИ (по наименованию не узнан) — проверьте';
  if (field === 'characteristics') {
    const params = r.src.ai_params.filter(
      (p) => !(AI_NUMERIC_FIELDS as readonly string[]).includes(p.key),
    );
    return params.length > 0
      ? `Справочные данные ИИ по марке техники — сверить с паспортом: ${params.map(paramLine).join('; ')}`
      : aiFieldDetails(field);
  }
  const p = r.src.ai_params.find((x) => x.key === field);
  return p
    ? `${NUM_LABEL[field]}: справочные данные ИИ по марке техники (проверочный ИИ: ${withUnit(p.check, p.unit)}) — сверить с паспортом`
    : aiFieldDetails(field);
}

/** «назначение: … · мобилизация: 12.10.2026, на трале» — что ещё взято из файла. */
function fileExtras(src: PtoImportedEquipment): string {
  const parts: string[] = [];
  if (src.purpose?.trim()) parts.push(`назначение: ${src.purpose.trim()}`);
  const mob = [
    src.mobilization_date ? fmtDate(src.mobilization_date) : '',
    src.mobilization_method?.trim() ?? '',
  ]
    .filter(Boolean)
    .join(', ');
  if (mob) parts.push(`мобилизация: ${mob}`);
  return parts.join(' · ');
}

/**
 * Пометка о количестве — у строки («количество не указано…», «количество «…» не
 * распознано») и у файла («Колонки «Количество» в файле нет…»). В справочнике
 * количества нет — такие пометки там не показываем.
 */
const isCountWarning = (w: string) => /^(количество\s|колонки «количество»)/i.test(w.trim());

/**
 * Пометка о значениях файла, которые у строки из справочника заменены записью
 * справочника: вид, ИИ, г/п, вылет, высота.
 */
const isFileValueWarning = (w: string) =>
  /^(ИИ[\s:]|вид техники|грузоподъёмность|г\/п\s|вылет\s|высота подъёма)/i.test(w.trim());

/** Под полем количества: «нет в файле» — колонки или значения нет; «не распознано» — значение есть, но не число. */
function countNote(src: PtoImportedEquipment): string | null {
  if (src.count != null) return null;
  return src.warnings.some((w) => isCountWarning(w) && /не распознан/i.test(w))
    ? 'не распознано'
    : 'нет в файле';
}

/** «1:05» — сколько идёт разбор. */
function elapsedText(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Конец предложения — точка, если её нет. */
function sentence(s: string): string {
  const t = s.trim();
  return /[.!?…]$/.test(t) ? t : `${t}.`;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** `wizard` — строки в таблицу техники мастера; `registry` — в справочник. */
  mode: 'wizard' | 'registry';
  /**
   * Справочник техники организации. Мастер: техника с тем же наименованием
   * берётся из справочника (вид, характеристики, ГВХ), количество и
   * мобилизация — из файла. Справочник: пометка «уже в справочнике».
   */
  registry: PtoEquipment[];
  /** Мастер: отмеченные строки — в конец таблицы техники. */
  onAdd?: (rows: EqRowDraft[]) => void;
  /** Мастер: сколько строк техники уже заполнено (всего — не больше 60). */
  existingCount?: number;
}

/**
 * «Загрузить технику файлом» (спека ППР по РД, §11): перечень техники Excel/CSV
 * раскладывается в строки на проверку. Недостающие характеристики дописывает ИИ
 * (каждое число подтверждает второй, проверочный ИИ) — такие значения с
 * пометкой «ИИ ×», их сверяют с паспортом. Мастер: отмеченные строки
 * добавляются в таблицу техники; справочник: новые записи создаются, у
 * существующих (по наименованию) дозаполняются пустые поля. Сам разбор ничего
 * не сохраняет.
 */
export function EquipmentImportDialog(props: Props) {
  // Состояние — во внутреннем компоненте: закрытие окна сбрасывает его и
  // прерывает незавершённый разбор.
  return props.open ? <ImportDialogBody {...props} /> : null;
}

function ImportDialogBody({ onClose, mode, registry, onAdd, existingCount = 0 }: Props) {
  const wizard = mode === 'wizard';
  const [importFile, { isLoading: reading }] = useImportPtoEquipmentFileMutation();
  const [saveBulk, { isLoading: saving }] = useSavePtoEquipmentBulkMutation();

  const [fileName, setFileName] = useState('');
  const [startedAt, setStartedAt] = useState(0);
  const [result, setResult] = useState<PtoEquipmentImport | null>(null);
  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  /** Файл выбран и проверяется до отправки — второй файл не принимаем. */
  const [checking, setChecking] = useState(false);
  const busy = useRef(false);
  const alive = useRef(true);
  const pending = useRef<ReturnType<typeof importFile> | null>(null);
  const now = useNow(reading, 1000);

  const byName = useMemo(() => {
    const m = new Map<string, PtoEquipment>();
    for (const e of registry) if (!m.has(normName(e.name))) m.set(normName(e.name), e);
    return m;
  }, [registry]);
  const byId = useMemo(() => new Map(registry.map((e) => [e.id, e])), [registry]);
  // Разбор идёт минутами — справочник к ответу берём свежий, а не на момент выбора файла.
  const byNameRef = useRef(byName);
  useEffect(() => {
    byNameRef.current = byName;
  }, [byName]);

  // Окно закрыли посреди разбора — запрос прерываем: результат уже не нужен.
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      pending.current?.abort();
    };
  }, []);

  // Файл, брошенный мимо зоны загрузки, браузер открыл бы вместо страницы —
  // и мастер с введёнными данными пропал бы. Пока окно открыто, бросок вне
  // зоны запрещён.
  useEffect(() => {
    const guard = (e: DragEvent) => {
      // Перетаскивание текста (выделенное слово в поле) не трогаем — только файлы.
      if (!Array.from(e.dataTransfer?.types ?? []).includes('Files')) return;
      const t = e.target;
      const el = t instanceof Element ? t : t instanceof Node ? t.parentElement : null;
      if (el?.closest('[data-equipment-drop]')) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
    };
    window.addEventListener('dragover', guard);
    window.addEventListener('drop', guard);
    return () => {
      window.removeEventListener('dragover', guard);
      window.removeEventListener('drop', guard);
    };
  }, []);

  /** Первая строка файла с тем же наименованием — повторы отмечаем (справочник). */
  const firstRowOf = useMemo(() => {
    const first = new Map<string, number>();
    for (const r of rows) {
      const k = normName(r.name);
      if (k && !first.has(k)) first.set(k, r.src.row);
    }
    return first;
  }, [rows]);

  const open = rows.filter((r) => !isDone(r));
  const taken = open.filter((r) => r.take);
  const errors = taken.flatMap((r) => rowErrors(r, wizard).map((e) => `Стр. ${r.src.row}: ${e}.`));
  const room = Math.max(0, MAX_EQUIPMENT_ROWS - existingCount);
  const overLimit = wizard && taken.length > room;
  const savedRows = rows.filter((r) => r.result !== null);
  const allSaved = !wizard && savedRows.length > 0 && taken.length === 0;
  const unsaved = taken.length > 0;
  const allTaken = open.length > 0 && taken.length === open.length;
  const colCount = 8 + (wizard ? 1 : 0) + (savedRows.length > 0 ? 1 : 0);

  const patch = (key: string, p: Partial<ReviewRow>) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...p } : r)));

  /**
   * Правка значения инженером — оно теперь его, пометка ИИ снимается.
   * Характеристика сохраняет пометку, пока в тексте остались числа ИИ.
   */
  function edit(r: ReviewRow, field: EquipmentAiField, value: string) {
    const keep = field === 'characteristics' && aiTextRemains(value, aiValuesOf(r));
    const p: Partial<ReviewRow> = {
      ai_fields: keep ? r.ai_fields : withoutAi(r.ai_fields, field),
    };
    p[field] = value;
    patch(r.key, p);
  }

  /**
   * «ИИ ×»: значение сверено с паспортом — пометка снимается, значение остаётся
   * (у характеристики из текста убирается метка «справочно (ИИ):»).
   */
  const clearAi = (r: ReviewRow, field: EquipmentAiField) => patch(r.key, verifiedPatch(r, field));

  function onName(r: ReviewRow, name: string) {
    const p: Partial<ReviewRow> = { name };
    // Наименование разошлось с записью справочника — это уже другая техника:
    // связь снимаем, значения остаются (как в таблице мастера).
    if (r.regId) {
      const linked = byId.get(r.regId);
      if (!linked || normName(linked.name) !== normName(name)) p.regId = null;
    }
    patch(r.key, p);
  }

  /** Вид, характеристики, г/п, вылет, высота и ГВХ — из справочника; количество и мобилизация — из файла. */
  const takeFromRegistry = (r: ReviewRow, e: PtoEquipment) => patch(r.key, registryValues(e));
  /** Вернуть значения строки файла (с дополнением ИИ); наименование — как сейчас в строке. */
  const takeFromFile = (r: ReviewRow) => patch(r.key, { ...fileValues(r.src), name: r.name });

  async function onFile(file: File) {
    // Занято сразу при выборе: до отправки ещё проверка формата, и второй файл,
    // выбранный в эту долю секунды, ушёл бы на разбор вместе с первым.
    if (busy.current) return;
    busy.current = true;
    setChecking(true);
    try {
      await readAndSend(file);
    } finally {
      busy.current = false;
      if (alive.current) setChecking(false);
    }
  }

  async function readAndSend(file: File) {
    setError('');
    if (file.size === 0) {
      setError('Файл пуст — выберите файл с перечнем техники.');
      return;
    }
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setError(`Файл больше ${MAX_FILE_MB} МБ — оставьте в нём только лист с перечнем техники.`);
      return;
    }
    const oldExcel = await isOldExcel(file);
    // Окно закрыли, пока читали начало файла, — на разбор не отправляем.
    if (!alive.current) return;
    if (oldExcel) {
      setError(OLD_XLS_TEXT);
      return;
    }
    const form = new FormData();
    form.append('file', file, file.name);
    setFileName(file.name);
    setStartedAt(Date.now());
    const req = importFile(form);
    pending.current = req;
    try {
      const res = await req.unwrap();
      const names = byNameRef.current;
      setResult(res.import);
      setRows(
        res.import.items.map((it, i) =>
          toReviewRow(it, i, wizard ? names.get(normName(it.name)) : undefined),
        ),
      );
      setShowErrors(false);
      setActionError('');
    } catch (e) {
      if (!isAbortError(e)) {
        setError(
          importErrorText(
            e,
            'Не удалось разобрать файл — нужна таблица с перечнем техники и шапкой.',
          ),
        );
      }
    } finally {
      if (pending.current === req) pending.current = null;
    }
  }

  /** Файл не того формата зона загрузки отбросила бы молча — объясняем. */
  function rejectWrongType(files: File[]) {
    if (files.length > 0 && !files.some((f) => matchesAccept(f.name, ACCEPT_EQUIPMENT_FILE))) {
      setError(`«${files[0].name}» — ${WRONG_TYPE_TEXT}`);
    }
  }

  function requestClose() {
    if (saving) return;
    if (reading || checking) {
      if (!window.confirm('Файл ещё обрабатывается. Прервать загрузку?')) return;
    } else if (
      unsaved &&
      !window.confirm(
        wizard
          ? 'Закрыть? Строки из файла не добавлены в перечень техники.'
          : 'Закрыть? Отмеченные строки не сохранены в справочник.',
      )
    ) {
      return;
    }
    onClose();
  }

  function reset() {
    if (unsaved && !window.confirm('Убрать строки этого файла и загрузить другой?')) return;
    setResult(null);
    setRows([]);
    setFileName('');
    setError('');
    setActionError('');
    setShowErrors(false);
  }

  function setAll(take: boolean) {
    setRows((prev) => prev.map((r) => (isDone(r) ? r : { ...r, take })));
  }

  /** Оставить отмеченными только первые `n` из отмеченных строк. */
  function takeFirst(n: number) {
    setRows((prev) => {
      let left = n;
      return prev.map((r) => {
        if (isDone(r) || !r.take) return r;
        if (left > 0) {
          left -= 1;
          return r;
        }
        return { ...r, take: false };
      });
    });
  }

  /** Текст колонки «Характеристики / Примечание» файла — в техническую характеристику. */
  function noteToCharacteristics(r: ReviewRow, note: string) {
    const characteristics = mergeFileNote(r.characteristics, note);
    const keep = aiTextRemains(characteristics, aiValuesOf(r));
    patch(r.key, {
      characteristics,
      ai_fields: keep ? r.ai_fields : withoutAi(r.ai_fields, 'characteristics'),
    });
  }

  function add() {
    if (errors.length > 0) {
      setShowErrors(true);
      return;
    }
    if (taken.length === 0 || overLimit) return;
    onAdd?.(taken.map(toEqRow));
    onClose();
  }

  async function save() {
    if (errors.length > 0) {
      setShowErrors(true);
      return;
    }
    if (taken.length === 0) return;
    if (taken.length > MAX_BULK) {
      setActionError(`За раз — не больше ${MAX_BULK} записей: снимите отметку с лишних строк.`);
      return;
    }
    setActionError('');
    const batch = taken;
    try {
      const res = await saveBulk({ items: batch.map(toBody) }).unwrap();
      // Итоги — в порядке отправленных записей.
      const byKey = new Map(batch.map((r, i) => [r.key, res.results[i] ?? null]));
      setRows((prev) =>
        prev.map((r) => (byKey.has(r.key) ? { ...r, result: byKey.get(r.key) ?? null } : r)),
      );
    } catch (e) {
      setActionError(equipmentErrorText(e, 'Не удалось сохранить технику в справочник.'));
    }
  }

  const aiMark = (r: ReviewRow, field: EquipmentAiField, locked: boolean) =>
    r.ai_fields.includes(field) ? (
      <CellMark>
        <AiMarkButton
          details={aiTitle(field, r)}
          disabled={locked}
          onClear={() => clearAi(r, field)}
        />
      </CellMark>
    ) : null;

  const fileWarnings = (result?.warnings ?? []).filter((w) => wizard || !isCountWarning(w));

  const counts = { created: 0, filled: 0, unchanged: 0, error: 0 };
  for (const r of savedRows) if (r.result) counts[r.result.status] += 1;

  return (
    <Dialog
      open
      title={wizard ? 'Загрузка техники из файла' : 'Загрузка техники в справочник'}
      onClose={requestClose}
      width={1280}
    >
      {!result ? (
        <div data-ppr="equipment-import-upload">
          <Lead>
            Перечень техники в Excel или CSV: колонки «Наименование», «Марка», «Количество»,
            «Характеристики»… Программа разложит строки по таблице, недостающие характеристики
            допишет ИИ по марке техники, а второй, проверочный ИИ подтвердит каждое число. Пока вы
            не проверите строки, ничего не сохраняется.
          </Lead>
          {reading ? (
            <AnalysisBar
              title="Читаю файл и дополняю характеристики ИИ — до 2–4 минут"
              hint={`${fileName} · прошло ${elapsedText(now - startedAt)}. Не закрывайте окно — строки появятся здесь.`}
            />
          ) : (
            <div
              data-equipment-drop=""
              onDropCapture={(e) => {
                const dt = e.dataTransfer;
                // Папку зона разбирает сама — берёт из неё первый подходящий файл.
                const dir = Array.from(dt.items ?? []).some(
                  (it) => it.kind === 'file' && it.webkitGetAsEntry?.()?.isDirectory,
                );
                if (!dir) rejectWrongType(Array.from(dt.files ?? []));
              }}
              onChangeCapture={(e) => {
                const input = e.target as HTMLInputElement;
                if (input.type === 'file') rejectWrongType(Array.from(input.files ?? []));
              }}
            >
              <FileDropZone
                accept={ACCEPT_EQUIPMENT_FILE}
                disabled={checking}
                primary={
                  checking ? 'Проверяю файл…' : 'Перетащите файл Excel или CSV с перечнем техники'
                }
                hint={`или нажмите, чтобы выбрать: .xlsx, .xlsm, .csv — до ${MAX_FILE_MB} МБ; старую книгу .xls (Excel 97–2003) пересохраните как .xlsx`}
                onFile={onFile}
              />
            </div>
          )}
          {error && <SpacedBanner $tone="danger">{error}</SpacedBanner>}
          <WizardFooter>
            <Button $variant="ghost" type="button" onClick={requestClose}>
              Отмена
            </Button>
          </WizardFooter>
        </div>
      ) : (
        <div data-ppr="equipment-import-review">
          <Lead>
            Файл «{result.filename}», лист «{result.sheet}», шапка — строка {result.header_row};
            строк техники: {result.items.length}.
            {result.columns.length > 0 && (
              <>
                <br />
                Узнаны колонки:{' '}
                {result.columns
                  .map((c) => `«${c.title}» (${COLUMN_LABEL[c.field] ?? c.field})`)
                  .join(', ')}
                .
              </>
            )}
          </Lead>
          {result.items.length > 0 && <AiSummaryBanner ai={result.ai} />}
          {fileWarnings.length > 0 && (
            <SpacedWarnBox>
              Обратите внимание:
              <ul>
                {fileWarnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </SpacedWarnBox>
          )}

          {rows.length === 0 ? (
            <Banner $tone="warning">
              В файле не нашлось строк техники. Проверьте, что перечень — таблица с шапкой
              («Наименование», «Марка», «Количество»…), и загрузите файл ещё раз.
            </Banner>
          ) : (
            <>
              <Lead>
                Снимите отметку с лишних строк, поправьте наименование, вид и параметры. «ИИ» —
                справочные данные по марке техники: наведите на пометку, чтобы увидеть ответ
                проверочного ИИ. Значение сверено с паспортом — нажмите «ИИ ×», пометка снимется;
                правка значения тоже снимает пометку.
                {wizard
                  ? ' Техника, которая уже есть в справочнике, берётся из него (с ГВХ); количество и мобилизация — из файла.'
                  : ' Запись с наименованием, которое уже есть в справочнике, не перезаписывается — у неё заполнятся только пустые поля.'}
              </Lead>
              <TableWrap $maxHeight="56vh">
                <ReviewTable>
                  <thead>
                    <tr>
                      <th className="c-check">
                        <input
                          type="checkbox"
                          checked={allTaken}
                          disabled={saving || open.length === 0}
                          aria-label="Отметить все строки"
                          ref={(el) => {
                            if (el) el.indeterminate = taken.length > 0 && !allTaken;
                          }}
                          onChange={(e) => setAll(e.target.checked)}
                        />
                      </th>
                      <th className="c-row" title="Строка в файле">
                        Стр.
                      </th>
                      <th className="c-name">Наименование и марка *</th>
                      <th className="c-kind">Вид</th>
                      <th className="c-text">Техническая характеристика</th>
                      <th className="c-num">Г/п max, т</th>
                      <th className="c-num">Вылет max, м</th>
                      <th className="c-num">Высота подъёма, м</th>
                      {wizard && <th className="c-count">Кол-во *</th>}
                      {savedRows.length > 0 && <th>Итог</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const done = isDone(r);
                      const locked = done || saving;
                      const match = byName.get(normName(r.name));
                      const chartPoints = r.load_chart.filter(
                        (p) => p.reach.trim() || p.cap.trim(),
                      ).length;
                      const lifting =
                        isLiftingKind(r.kind) ||
                        AI_NUMERIC_FIELDS.some((f) => r[f].trim() !== '') ||
                        chartPoints > 0;
                      const note = r.src.file_note?.trim() ?? '';
                      const noteShown = note !== '' && !hasFileNote(r.characteristics, note);
                      const canMerge =
                        noteShown &&
                        !locked &&
                        mergeFileNote(r.characteristics, note) !== r.characteristics.trim();
                      const extras = wizard ? fileExtras(r.src) : '';
                      const firstRow = firstRowOf.get(normName(r.name));
                      const dupOf =
                        !wizard && !done && firstRow !== undefined && firstRow !== r.src.row
                          ? firstRow
                          : null;
                      const staleAi =
                        r.ai_fields.length > 0 && normName(r.name) !== normName(r.valuesFor);
                      const warnings = r.src.warnings.filter(
                        (w) =>
                          (wizard || !isCountWarning(w)) &&
                          !(r.fromRegistry && isFileValueWarning(w)),
                      );
                      const rowError =
                        r.result?.status === 'error' ? bulkErrorText(r.result.error) : '';
                      const hasNotes = warnings.length > 0 || rowError !== '';
                      const tone = rowError ? 'danger' : !r.take && !done ? 'muted' : undefined;
                      const meta = r.result ? RESULT_META[r.result.status] : null;
                      const countHint = wizard ? countNote(r.src) : null;
                      return (
                        <Fragment key={r.key}>
                          <tr
                            data-tone={tone}
                            data-split={hasNotes ? 'true' : undefined}
                            data-import-row={r.src.row}
                          >
                            <td className="c-check">
                              <input
                                type="checkbox"
                                checked={r.take}
                                disabled={locked}
                                aria-label={`Взять строку ${r.src.row} файла`}
                                onChange={(e) => patch(r.key, { take: e.target.checked })}
                              />
                            </td>
                            <td className="c-row">{r.src.row}</td>
                            <td>
                              <CellInput
                                value={r.name}
                                disabled={locked}
                                aria-label="Наименование и марка"
                                $invalid={
                                  r.take &&
                                  !done &&
                                  (!r.name.trim() || textLength(r.name) > EQ_NAME_MAX)
                                }
                                onChange={(e) => onName(r, e.target.value)}
                              />
                              {wizard && (r.regId || match || r.fromRegistry) && (
                                <CellMark>
                                  {(r.regId || match) && (
                                    <Tag
                                      $tone="info"
                                      title="В справочнике есть техника с таким наименованием"
                                    >
                                      есть в справочнике
                                    </Tag>
                                  )}
                                  {match && r.regId !== match.id && (
                                    <NoteButton
                                      type="button"
                                      disabled={locked}
                                      title="Вид, характеристики, г/п, вылет, высоту и ГВХ взять из справочника; количество и мобилизация — из файла"
                                      onClick={() => takeFromRegistry(r, match)}
                                    >
                                      взять из справочника
                                    </NoteButton>
                                  )}
                                  {r.fromRegistry && (
                                    <NoteButton
                                      type="button"
                                      disabled={locked}
                                      title="Вид, характеристики, г/п, вылет и высоту взять из файла (с дополнением ИИ)"
                                      onClick={() => takeFromFile(r)}
                                    >
                                      взять из файла
                                    </NoteButton>
                                  )}
                                </CellMark>
                              )}
                              {wizard && r.fromRegistry && (
                                <CellMark>
                                  значения из справочника
                                  {chartPoints > 0
                                    ? ` · ГВХ ${chartPoints} ${pointsWord(chartPoints)}`
                                    : ''}
                                </CellMark>
                              )}
                              {!wizard && !done && match && (
                                <CellMark>
                                  <Tag
                                    $tone="info"
                                    title="В справочнике уже есть запись с таким наименованием — у неё заполнятся только пустые поля"
                                  >
                                    уже в справочнике
                                  </Tag>
                                </CellMark>
                              )}
                              {dupOf !== null && (
                                <CellMark>
                                  <Tag
                                    $tone="warning"
                                    title="В файле уже есть строка с таким наименованием — в справочнике это одна запись: первая строка её создаст, следующие только дозаполнят пустые поля"
                                  >
                                    повтор строки {dupOf}
                                  </Tag>
                                </CellMark>
                              )}
                              {staleAi && (
                                <CellMark>
                                  <WarnText>
                                    характеристики ИИ — для прежней марки, проверьте
                                  </WarnText>
                                </CellMark>
                              )}
                              {noteShown && (
                                <CellMark>
                                  <span>из файла: {note}</span>
                                  {canMerge && (
                                    <NoteButton
                                      type="button"
                                      title="Перенести текст из файла в техническую характеристику"
                                      onClick={() => noteToCharacteristics(r, note)}
                                    >
                                      в характеристику
                                    </NoteButton>
                                  )}
                                </CellMark>
                              )}
                              {extras && <CellMark>{extras}</CellMark>}
                            </td>
                            <td>
                              <CellSelect
                                value={r.kind}
                                disabled={locked}
                                aria-label="Вид техники"
                                onChange={(e) => edit(r, 'kind', e.target.value)}
                              >
                                {!KIND_CODES.has(r.kind) && (
                                  <option value={r.kind}>{r.kind}</option>
                                )}
                                {EQUIPMENT_KINDS.map(([k, label]) => (
                                  <option key={k} value={k}>
                                    {label}
                                  </option>
                                ))}
                              </CellSelect>
                              {aiMark(r, 'kind', locked)}
                            </td>
                            <td>
                              <CellArea
                                value={r.characteristics}
                                rows={2}
                                disabled={locked}
                                aria-label="Техническая характеристика"
                                $invalid={textLength(r.characteristics) > EQ_CHARACTERISTICS_MAX}
                                onChange={(e) => edit(r, 'characteristics', e.target.value)}
                              />
                              {aiMark(r, 'characteristics', locked)}
                            </td>
                            {lifting
                              ? AI_NUMERIC_FIELDS.map((f) => (
                                  <td key={f}>
                                    <CellInput
                                      value={r[f]}
                                      inputMode="decimal"
                                      disabled={locked}
                                      aria-label={NUM_LABEL[f]}
                                      $invalid={eqNumError(f, r[f]) !== null}
                                      onChange={(e) => edit(r, f, e.target.value)}
                                    />
                                    {aiMark(r, f, locked)}
                                  </td>
                                ))
                              : AI_NUMERIC_FIELDS.map((f) => (
                                  <td key={f} className="dash">
                                    —
                                  </td>
                                ))}
                            {wizard && (
                              <td>
                                <CellInput
                                  value={r.count}
                                  inputMode="numeric"
                                  disabled={locked}
                                  aria-label="Количество"
                                  $invalid={r.take && !isEqCount(r.count)}
                                  onChange={(e) => patch(r.key, { count: e.target.value })}
                                />
                                {countHint && <CellMark>{countHint}</CellMark>}
                              </td>
                            )}
                            {savedRows.length > 0 && (
                              <td className="nowrap">
                                {meta && (
                                  <Tag $tone={meta.tone} title={meta.title}>
                                    {meta.label}
                                  </Tag>
                                )}
                              </td>
                            )}
                          </tr>
                          {hasNotes && (
                            <tr className="notes" data-tone={tone}>
                              <td colSpan={2} />
                              <td colSpan={colCount - 2}>
                                {rowError && <RowNote $tone="danger">{rowError}</RowNote>}
                                {warnings.map((w, i) => (
                                  <RowNote key={i} $tone="warning">
                                    {w}
                                  </RowNote>
                                ))}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </ReviewTable>
              </TableWrap>
            </>
          )}

          {overLimit && (
            <SpacedBanner $tone="warning">
              <BannerRow>
                <span>
                  {existingCount === 0
                    ? `В перечне техники мастера — не больше ${MAX_EQUIPMENT_ROWS} строк, отмечено ${taken.length}. Снимите отметку с лишних строк.`
                    : room === 0
                      ? `В перечне техники мастера уже ${MAX_EQUIPMENT_ROWS} строк — больше добавить нельзя. Удалите лишние строки в таблице мастера.`
                      : `В перечне техники мастера уже ${existingCount} ${plural(existingCount, ['строка', 'строки', 'строк'])} — можно добавить ещё ${room} (всего не больше ${MAX_EQUIPMENT_ROWS}), отмечено ${taken.length}.`}
                </span>
                {room > 0 && (
                  <Button
                    $variant="secondary"
                    $size="sm"
                    type="button"
                    title={`Оставить отмеченными только первые ${room} из отмеченных строк`}
                    onClick={() => takeFirst(room)}
                  >
                    Отметить первые {room}
                  </Button>
                )}
              </BannerRow>
            </SpacedBanner>
          )}
          {showErrors && errors.length > 0 && (
            <SpacedBanner $tone="danger">
              <ErrorList>
                {errors.slice(0, MAX_SHOWN_ERRORS).map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
                {errors.length > MAX_SHOWN_ERRORS && (
                  <li>…и ещё {errors.length - MAX_SHOWN_ERRORS}</li>
                )}
              </ErrorList>
            </SpacedBanner>
          )}
          {savedRows.length > 0 && (
            <SpacedBanner $tone={counts.error > 0 ? 'warning' : 'success'}>
              Справочник: создано {counts.created}, дозаполнено {counts.filled}, без изменений{' '}
              {counts.unchanged}
              {counts.error > 0
                ? `; не сохранено ${counts.error} — исправьте эти строки и сохраните ещё раз.`
                : '.'}
            </SpacedBanner>
          )}
          {actionError && <SpacedBanner $tone="danger">{actionError}</SpacedBanner>}

          <WizardFooter>
            <Actions>
              <Button $variant="ghost" type="button" disabled={saving} onClick={requestClose}>
                {savedRows.length > 0 ? 'Закрыть' : 'Отмена'}
              </Button>
              <Button $variant="secondary" type="button" disabled={saving} onClick={reset}>
                Загрузить другой файл
              </Button>
            </Actions>
            <Actions>
              {open.length > 0 && (
                <Muted as="span">
                  Отмечено: {taken.length} из {open.length}
                </Muted>
              )}
              {wizard ? (
                <Button
                  $variant="primary"
                  type="button"
                  disabled={taken.length === 0 || overLimit}
                  onClick={add}
                >
                  Добавить в перечень ({taken.length})
                </Button>
              ) : allSaved ? (
                <Button $variant="primary" type="button" onClick={onClose}>
                  Готово
                </Button>
              ) : (
                <Button
                  $variant="primary"
                  type="button"
                  disabled={saving || taken.length === 0}
                  onClick={save}
                >
                  {saving ? 'Сохраняю…' : `Сохранить в справочник (${taken.length})`}
                </Button>
              )}
            </Actions>
          </WizardFooter>
        </div>
      )}
    </Dialog>
  );
}

/** Как прошло дополнение ИИ — над таблицей проверки (текст сервера — без повторов). */
function AiSummaryBanner({ ai }: { ai: PtoEquipmentAiSummary }) {
  const values = (n: number) => `${n} ${plural(n, ['значение', 'значения', 'значений'])}`;
  const verify =
    'Это справочные данные по марке техники — сверьте их с паспортами машин (в таблице отмечены «ИИ»).';
  switch (ai.status) {
    case 'failed':
      return (
        <Banner $tone="warning">
          {sentence(ai.message || 'ИИ не ответил — характеристики как в файле')} Недостающие
          характеристики допишите по паспортам машин.
        </Banner>
      );
    case 'skipped':
      return (
        <Banner $tone="info">
          {sentence(ai.message || 'Дополнять нечего — характеристики взяты из файла')}
        </Banner>
      );
    case 'partial':
      return (
        <Banner $tone="warning">
          {sentence(
            ai.message ||
              'ИИ ответил не по всем строкам — где ответа нет, характеристики как в файле',
          )}
          {ai.accepted > 0
            ? ` Дописано ${values(ai.accepted)} — каждое подтвердил проверочный ИИ. ${verify}`
            : ''}
        </Banner>
      );
    default: {
      const rejected = Math.max(ai.asked - ai.accepted, 0);
      if (ai.accepted === 0) {
        return (
          <Banner $tone="warning">
            {ai.asked > 0
              ? `ИИ предложил ${values(ai.asked)}, но проверочный ИИ не подтвердил ни одного`
              : 'ИИ ничего не дописал'}{' '}
            — характеристики как в файле, недостающие допишите по паспортам машин.
          </Banner>
        );
      }
      return (
        <Banner $tone="info">
          ИИ дописал {values(ai.accepted)} — каждое подтвердил проверочный ИИ
          {rejected > 0 ? `; не подтверждено и не внесено: ${rejected}` : ''}. {verify}
        </Banner>
      );
    }
  }
}

const Lead = styled(Muted)`
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  line-height: 1.5;
`;

const SpacedBanner = styled(Banner)`
  margin-top: ${({ theme }) => theme.spacing(3)};
  margin-bottom: 0;
`;

const BannerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const SpacedWarnBox = styled(WarnBox)`
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
`;

/** Таблица проверки строк файла: поля ввода в ячейках, пометки — под строкой. */
const ReviewTable = styled(DataTable)`
  min-width: 1120px;

  th {
    white-space: normal;
    vertical-align: bottom;
  }
  th.c-name {
    min-width: 240px;
  }
  th.c-kind {
    min-width: 170px;
  }
  th.c-text {
    min-width: 250px;
  }
  th.c-num {
    width: 82px;
  }
  th.c-count {
    width: 72px;
  }
  td.c-check,
  td.c-row,
  td.dash {
    padding-top: 11px;
  }
  td.c-row,
  td.dash {
    color: ${({ theme }) => theme.colors.text.muted};
  }
  td.dash {
    text-align: center;
  }
  tbody tr[data-split='true'] td {
    border-bottom: none;
  }
  tbody tr.notes td {
    padding-top: 0;
  }
`;

const NoteButton = styled(LinkButton)`
  font-size: 11px;

  &:disabled {
    cursor: default;
    opacity: 0.6;
    text-decoration: none;
  }
`;

const WarnText = styled.span`
  color: ${({ theme }) => theme.colors.status.warning};
`;

const RowNote = styled.div<{ $tone: 'warning' | 'danger' }>`
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme, $tone }) => theme.colors.status[$tone]};
`;
